import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, Share, Plus, X, Smartphone } from "lucide-react";
import { applyDynamicManifest, detectPersona, PERSONA_LIST } from "@/lib/pwaManifest";

/** Detects platform for showing platform-specific instructions. */
function getPlatform() {
    const ua = navigator.userAgent || "";
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
    if (/Android/i.test(ua)) return "android";
    return "desktop";
}

function isStandalone() {
    return (
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );
}

const DISMISS_KEY_PREFIX = "c24_install_dismissed_";

export default function InstallPrompt() {
    const location = useLocation();
    const [persona, setPersona] = useState(null);
    const [deferred, setDeferred] = useState(null);
    const [show, setShow] = useState(false);
    const [platform] = useState(getPlatform());

    // Apply persona-specific manifest on every route change
    useEffect(() => {
        const key = detectPersona();
        const p = applyDynamicManifest(key);
        setPersona({ key, ...p });
    }, [location.pathname]);

    // Capture beforeinstallprompt (Chrome/Edge/Samsung)
    useEffect(() => {
        const handler = (e) => {
            e.preventDefault();
            setDeferred(e);
        };
        window.addEventListener("beforeinstallprompt", handler);
        return () => window.removeEventListener("beforeinstallprompt", handler);
    }, []);

    // Decide whether to show the prompt
    useEffect(() => {
        if (!persona) return;
        if (isStandalone()) return; // already installed
        const dismissed = localStorage.getItem(DISMISS_KEY_PREFIX + persona.key);
        if (dismissed) {
            // Re-show after 3 days
            if (Date.now() - Number(dismissed) < 3 * 24 * 60 * 60 * 1000) return;
        }
        // Delay a little so the user sees the page first
        const t = setTimeout(() => setShow(true), 2500);
        return () => clearTimeout(t);
    }, [persona, deferred]);

    if (!persona || !show) return null;

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY_PREFIX + persona.key, String(Date.now()));
        setShow(false);
    };

    const handleInstall = async () => {
        if (deferred) {
            deferred.prompt();
            const { outcome } = await deferred.userChoice;
            if (outcome === "accepted") {
                setShow(false);
            }
            setDeferred(null);
        }
    };

    const isAr = document.documentElement.dir === "rtl";

    return (
        <div
            className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4 pointer-events-none"
            data-testid="install-prompt"
            dir={isAr ? "rtl" : "ltr"}
        >
            <div className="max-w-md mx-auto pointer-events-auto bg-card border border-primary/30 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-500">
                <div className="bg-gradient-to-r from-primary/20 to-primary/5 p-4 flex items-start gap-3">
                    <div className="bg-primary/20 rounded-xl p-2.5 flex-shrink-0">
                        <Smartphone className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-base text-foreground truncate" data-testid="install-app-name">
                            {persona.name}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {isAr ? "ثبّت التطبيق على شاشتك الرئيسية" : "Install this app on your home screen"}
                        </p>
                    </div>
                    <button
                        onClick={dismiss}
                        className="text-muted-foreground hover:text-foreground p-1"
                        data-testid="install-dismiss"
                        aria-label="dismiss"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="p-4 space-y-3">
                    {platform === "ios" ? (
                        <div className="space-y-2 text-sm">
                            <p className="text-muted-foreground mb-2">
                                {isAr ? "للتثبيت على iPhone:" : "To install on iPhone:"}
                            </p>
                            <div className="flex items-center gap-2">
                                <span className="bg-primary/15 text-primary text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">1</span>
                                <span className="flex items-center gap-1">
                                    {isAr ? "اضغط على زر" : "Tap the"}
                                    <Share className="h-4 w-4 text-blue-500 mx-1" />
                                    {isAr ? "أسفل الشاشة" : "share button"}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="bg-primary/15 text-primary text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">2</span>
                                <span className="flex items-center gap-1">
                                    {isAr ? "اختر" : "Choose"}
                                    <span className="font-medium">"{isAr ? "إضافة إلى الشاشة الرئيسية" : "Add to Home Screen"}"</span>
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="bg-primary/15 text-primary text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">3</span>
                                <span className="flex items-center gap-1">
                                    {isAr ? "اضغط" : "Tap"} <span className="font-medium">"{isAr ? "إضافة" : "Add"}"</span>
                                </span>
                            </div>
                        </div>
                    ) : deferred ? (
                        <button
                            onClick={handleInstall}
                            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl py-3 px-4 font-medium flex items-center justify-center gap-2 transition-all active:scale-95"
                            data-testid="install-now-btn"
                        >
                            <Download className="h-5 w-5" />
                            {isAr ? "تثبيت الآن" : "Install Now"}
                        </button>
                    ) : platform === "android" ? (
                        <div className="space-y-2 text-sm">
                            <p className="text-muted-foreground mb-2">
                                {isAr ? "للتثبيت على Android:" : "To install on Android:"}
                            </p>
                            <div className="flex items-center gap-2">
                                <span className="bg-primary/15 text-primary text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">1</span>
                                <span>{isAr ? "اضغط على القائمة ⋮ في Chrome" : "Tap menu ⋮ in Chrome"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="bg-primary/15 text-primary text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">2</span>
                                <span className="flex items-center gap-1">
                                    {isAr ? "اختر" : "Choose"}
                                    <span className="font-medium">"{isAr ? "تثبيت التطبيق" : "Install app"}"</span>
                                    <Plus className="h-3 w-3" />
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="text-sm text-muted-foreground">
                            {isAr
                                ? "افتح الرابط في Chrome على هاتفك للتثبيت"
                                : "Open this link on your phone in Chrome to install"}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Expose persona list for debugging / future settings page
export { PERSONA_LIST };
